import FormField from 'components/FormField';
import {
  Select,
  Radio,
  Input,
  message,
  Button,
  Form,
  SelectProps,
  Space,
  Cascader,
  Popconfirm,
} from 'antd';
import { tradeList as getTradeList, productList } from 'services/api/conversation';
import {
  useDebounce,
  useEventListener,
  useLatest,
  useMount,
  useRequest,
  useUpdateEffect,
} from 'ahooks';
import { useTranslate } from '@tolgee/react';
import { useMemo, useState, useContext, useEffect, useRef, useCallback, ReactNode } from 'react';
import styled from '@emotion/styled';
import { css, cx } from '@emotion/css';
import { useFormikContext } from 'formik';
import copyText from 'utils/copyText';
import Icon, { CopyOutlined, CheckOutlined, MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { ItemHeader } from './Layout';
import TextWithImageInput from 'components/TextWithImageInput';
import CustomUpload, { CustomUploadRef } from './Upload';
import {
  convertMessageToTextAndImage,
  convertTextAndImageToMessage,
  getReturnSnList,
  getValidTypeSource,
  isReturnOrderEnable,
} from './helper';
import { TodoRecordImplement, TodoRecord } from './OperationLog';
import { ReadonlyContext, ToDoBundleContext } from './context';
import { useResultTypeTipsLabel } from 'views/Todo/hooks/useLabel';
import { useAssistants } from './useApi';
import useSmartField from 'hooks/useSmartField';
import { officialTypes, useTodoType } from 'views/Aide/Basis/Todo/TodoType';
import { TypeSourceContext } from './TypeSourceContext';
import { useResultForm } from './useResultForm';
import { ReactComponent as MoreDownSvg } from '../assets/more-down.svg';
import { useGetPermission } from 'hooks/business/useNewPermisson';
import {
  map,
  get,
  findIndex,
  filter,
  find,
  reduce,
  uniqBy,
  groupBy,
  sumBy,
  debounce,
  keys,
} from 'lodash-es';
import { conv2TradeExternalBuyerId } from 'utils/search';
import SafeImage from 'components/SafeImage';
import { getSortedInfo, SortTodoButton } from 'views/Aide/Basis/Todo/SortTodo';
import { todoStatusList } from '../../../Todo/service';
import { useConversationToggleTextareaProps } from 'hooks/business/conversationsHooks/useConversationHotKey';
import TemplateNode, { HotkeyTips } from './TemplateNode';
import StatusSwitch from '../../../../components/StatusSwitch';
import { useQuery } from '@tanstack/react-query';
import { ErpOrderResult, getErpOrder } from './api';
import { useReplyMsgs, ReplyMsgsFormValues } from './useReplyMsgs';
import TradeStatus from '../TradeStatus';
import { useOrgConfigStateValue } from 'hooks/business/useOrgConfig';
import ErpOrder from './ErpOrder';
import { eventBus } from 'services/eventBus';
import { ProductListParamsKeywordType as KeywordType } from 'services/api/conversation';
import produce from 'immer';
import { TagProduct, ProductTagList } from 'beisi/TagProduct';
import posthog from '@leyan/lytics';
import { useTodoSubStatus } from './hook';

export const RelatedTradeItem = () => {
  const { t } = useTranslate();

  const { values } = useFormikContext<Todo.TodoInstance>();

  const { id } = values;

  const [updateErpOrderKey, setUpdateErpOrderKey] = useState(0);

  const tradeIdUpdatingRef = useRef(false);

  useEffect(() => {
    const handler = (param: string) => {
      if (param === id && tradeIdUpdatingRef.current) {
        setUpdateErpOrderKey((pre) => pre + 1);
        tradeIdUpdatingRef.current = false;
      }
    };

    eventBus.on('TODO_UPDATE', handler);

    return () => eventBus.off('TODO_UPDATE', handler);
  }, [id]);

  const { data: erpOrder, refreshAsync: refreshErpOrder } = useRequest(() => getErpOrder(id), {
    ready: Boolean(id),
    refreshDeps: [id, updateErpOrderKey],
  });

  const disabled = useContext(ReadonlyContext) || values.todoStatus === 3;

  return (
    <RelatedContainer>
      <ItemHeader title={t('关联订单&商品')} />
      <RelatedDescription
        disabled={disabled}
        erpMode
        erpOrder={erpOrder}
        erpOrderChangeEffect={() => {
          tradeIdUpdatingRef.current = true;
        }}
        refreshErpOrder={refreshErpOrder}
      />
    </RelatedContainer>
  );
};

const RelatedContainer = styled.div`
  > .ant-select {
    display: block;
    width: 100%;
    margin: 12px 0;
  }
`;

const RelatedTrade = styled.div`
  display: flex;
  align-items: center;
  > div {
    margin-left: 8px;
  }
`;

// 问题描述
export const Description = () => {
  const { t, getResultTypeTipsLabel } = useResultTypeTipsLabel();

  const { whiteListConfig: { ERP } = {} } = useOrgConfigStateValue();

  const { values, errors, dirty, setValues, handleSubmit } = useFormikContext<Todo.TodoInstance>();

  const { platform, storeId, description, type, operateResult } = values;

  const { text, images } = useMemo(() => {
    return convertMessageToTextAndImage(description);
  }, [description]);

  const readonly = useContext(ReadonlyContext);
  const disabled = values.todoStatus === 3 || readonly;

  const disabledForType6 = type === 6;
  const { conversationId } = useContext(ToDoBundleContext);

  const customUploadRef = useRef<CustomUploadRef>(null);

  const conversationToggleTextAreaProps = useConversationToggleTextareaProps();

  return (
    <div className="mt-[18px]">
      <ItemHeader title={t('todo_info')} />
      <Form.Item label={t('ViewBlockList.filter.other.notice')}>
        <TextWithImageInput
          text={text}
          images={images}
          onTextChange={(value) => {
            setValues({ ...values, description: convertTextAndImageToMessage(value, images) });
          }}
          onImagesChange={(value) => {
            setValues({ ...values, description: convertTextAndImageToMessage(text, value) });
            handleSubmit();
          }}
          placeholder={getResultTypeTipsLabel(operateResult)}
          disabled={disabled}
          error={!!errors.description}
          textareaProps={{
            ...conversationToggleTextAreaProps,
            onBlur: () => {
              dirty && handleSubmit();
              conversationToggleTextAreaProps.onBlur();
            },
          }}
          renderImageUpload={({ value, onChange, disabled: uploadDisabled }) => (
            <CustomUpload
              ref={customUploadRef}
              disabled={uploadDisabled}
              selfHosted
              platform={platform}
              internalStoreId={storeId}
              conversationId={conversationId}
              onChange={onChange}
              value={value}
              direct={false}
            />
          )}
          onPasteImage={(file) => customUploadRef.current?.handUpload(file)}
        />
        {errors.description && <div className="text-[#f5222d]">{errors.description}</div>}
      </Form.Item>
      {!disabledForType6 && (!ERP || ![1, 2].includes(type)) && (
        <RelatedDescription disabled={disabled} />
      )}
    </div>
  );
};

// 选择状态
export const Status = ({ color }: { color: string }) => {
  const { t } = useTranslate();
  const { values, setValues, handleSubmit } = useFormikContext<Todo.TodoInstance>();
  const { taskId } = useContext(TypeSourceContext);
  const { data, loading } = useRequest(() => todoStatusList(taskId!), {
    ready: !!taskId,
    refreshDeps: [taskId],
  });

  const childStatus = useTodoSubStatus(data || [], values.todoStatus);

  if (values.todoStatus === 3) {
    return null;
  }

  return (
    <StatusContainer color={color}>
      <Space size={10}>
        <Select
          dropdownMatchSelectWidth={false}
          value={values.todoStatus === 1 ? t('todo.待认领') : values.todoStatus}
          onChange={(value) => {
            setValues({
              ...values,
              todoStatus: value as any,
              subStatus: '',
            });
            handleSubmit();
          }}
        >
          <Select.Option value={4}>{t('review.待处理')}</Select.Option>
          <Select.Option value={2}>{t('review.处理中')}</Select.Option>
        </Select>
        {childStatus.length > 0 && (
          <Select
            dropdownMatchSelectWidth={false}
            value={values.subStatus || undefined}
            allowClear
            showSearch
            filterOption={(input, option) => {
              return (option?.children ?? '').includes(input);
            }}
            onClear={() => {
              setValues({
                ...values,
                subStatus: '',
              });
              handleSubmit();
            }}
            placeholder={t('请选择子状态')}
            loading={loading}
            onChange={(value) => {
              setValues({
                ...values,
                subStatus: value,
              });
              handleSubmit();
            }}
          >
            {childStatus.map((each) => {
              return (
                <Select.Option key={each.subStatus} value={each.subStatus}>
                  {each.subStatusLabel}
                </Select.Option>
              );
            })}
          </Select>
        )}
      </Space>
    </StatusContainer>
  );
};

const StatusContainer = styled.div<{ color: string }>`
  .ant-select {
    min-width: 60px;
  }
  .ant-space-item:first-of-type {
    .ant-select {
      color: ${({ color }) => color};
      > div {
        border-color: ${({ color }) => color};
      }
    }
  }
`;

// 需要二次确认的 Shopee 争议类结果（选后会影响解决方案）
const SHOPEE_DISPUTE_RESULT_VALUES = ['SUBMITTED_DISPUTE', 'DISPUTE_SUCCESS', 'DISPUTE_FAIL'];
const SHOPEE_DISPUTE_SET = new Set(SHOPEE_DISPUTE_RESULT_VALUES);

// 处理结果（容器：只做编排，分支收敛为「只读展示 / 可编辑」）
export const Result = () => {
  const { t } = useTranslate();
  const { typeSource } = useContext(TypeSourceContext);
  const readonly = useContext(ReadonlyContext);
  const {
    resultOptions,
    displayText,
    isReadonly,
    isOtherSelected,
    errors,
    disabled,
    values: { id, operateResult, customOperateResult },
    onChecked,
    onCustomResultChange,
    onCustomResultBlur,
  } = useResultForm({ typeSource: typeSource ?? [], readonly: !!readonly });

  return (
    <div>
      <ItemHeader title={t('choose_solution')} />
      {isReadonly ? (
        <div className="bg-[#f0f7ff] mt-[9px] leading-[20px] py-[6px] px-[9px] text-[#515359]">
          {displayText}
        </div>
      ) : (
        <div className="mt-[9px]" id="operateResult">
          <Radio.Group
            value={operateResult}
            disabled={disabled}
            onChange={({ target: { value } }) => onChecked(value)}
            style={{ width: '100%' }}
          >
            {resultOptions.map((each, index) => (
              <Solution
                key={each.value}
                id={id}
                index={index}
                operateResult={operateResult}
                result={each}
                onChecked={onChecked}
                needShopeeConfirm={SHOPEE_DISPUTE_SET.has(each.value)}
              />
            ))}
            <Solution
              id={id}
              index={resultOptions.length}
              operateResult={operateResult}
              result={{ value: 'OTHER', label: t('resultFallback.OTHER') }}
              onChecked={onChecked}
              needShopeeConfirm={false}
            />
          </Radio.Group>
          {errors.operateResult && <div className="text-[#f5222d]">{errors.operateResult}</div>}
          {isOtherSelected && (
            <div className="pl-[23px] mt-[11px]">
              <Input
                value={customOperateResult}
                disabled={disabled}
                onChange={({ target: { value } }) => onCustomResultChange(value)}
                onBlur={onCustomResultBlur}
                placeholder={t('trade.请输入')}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const MAX_HOTKEY_INDEX = 9;

type SolutionProps = {
  id: string;
  index: number;
  operateResult: string;
  result: { value: string; label: string };
  onChecked: (value: string) => void;
  needShopeeConfirm: boolean;
};

const Solution = ({
  id,
  index,
  operateResult,
  result: { value, label },
  onChecked,
  needShopeeConfirm,
}: SolutionProps) => {
  const { t } = useTranslate();
  const [shopeeConfirmVisible, setShopeeConfirmVisible] = useState(false);

  const hotkeyEnabled = index <= MAX_HOTKEY_INDEX;
  const hotkeyNumber = (index + 1) % 10;
  const hotkeyTips = hotkeyEnabled ? `ctrl+${hotkeyNumber}` : null;

  useEventListener('keydown', (e) => {
    const { key, ctrlKey } = e;
    if (!hotkeyEnabled || !ctrlKey || operateResult === value || isNaN(Number(key))) return;
    e.preventDefault();
    if (Number(key) === hotkeyNumber) {
      posthog.capture('$hotKey', { hotKey: `Ctrl+${key}`, todoId: id });
      if (!needShopeeConfirm) {
        onChecked(value);
      } else if (shopeeConfirmVisible) {
        onChecked(value);
      } else {
        setShopeeConfirmVisible(true);
      }
    } else if (shopeeConfirmVisible) {
      setShopeeConfirmVisible(false);
    }
  });

  const radioCommonProps = {
    value,
    style: {
      marginRight: 0,
      wordBreak: 'break-all' as const,
      ...(needShopeeConfirm ? {} : { width: '50%' }),
    },
    onKeyDown: (e: React.KeyboardEvent) => e.preventDefault(),
    children: (
      <>
        {label}
        {hotkeyTips && <HotkeyText>{hotkeyTips}</HotkeyText>}
      </>
    ),
  };

  if (needShopeeConfirm) {
    return (
      <Popconfirm
        disabled={operateResult === value}
        visible={shopeeConfirmVisible}
        onVisibleChange={setShopeeConfirmVisible}
        placement="left"
        okText={`${t('phrase.确定')}${hotkeyTips ? ` (${hotkeyTips})` : ''}`}
        title={t('向Shopee争议后，解决方案将自动调整，是否确认修改？')}
        onConfirm={(e) => {
          e?.stopPropagation();
          onChecked(value);
        }}
      >
        <div className="w-[50%] inline-block">
          <Radio
            className="pointer-events-none"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
            {...radioCommonProps}
          />
        </div>
      </Popconfirm>
    );
  }

  return <Radio {...radioCommonProps} />;
};

const HotkeyText = styled.span`
  margin-left: 4px;
  font-size: 12px;
  color: #84868c;
  white-space: nowrap;
`;

// 回复推荐区域：纯展示组件，仅负责 UI 与交互触发，不包含业务判断与副作用
type ReplyMsgsViewProps = ReturnType<typeof useReplyMsgs>;

function ReplyMsgsView(props: ReplyMsgsViewProps) {
  const { t } = useTranslate();
  const {
    containerRef,
    templateRef,
    filterSelfTemplate,
    filterTemplates,
    activeIndex,
    setActiveIndex,
    showOriginalText,
    onShowOriginalTextChange,
    resultTypeLabel,
  } = props;

  const templateNodes = filterTemplates.map((item, index) => {
    const active = activeIndex === index;
    return (
      <div
        key={item.resultType}
        onMouseEnter={() => setActiveIndex(index)}
        style={{
          marginBottom: 18,
          border: active ? 'solid 1px #336FFF' : 'solid 1px transparent',
          background: active ? '#F0F7FF' : '#F7F9FA',
        }}
      >
        <TemplateNode index={index} active={active} template={item} doubleMode={showOriginalText} />
      </div>
    );
  });

  return (
    <div ref={containerRef} className="mt-[16px]">
      <ItemHeader
        title={`${resultTypeLabel} - ${t('aide.推荐回复')}`}
        right={
          <div className="flex gap-[8px] items-center">
            <span className="text-[12px] text-[#84868C]">{t('展示原文')}</span>
            <StatusSwitch
              size="small"
              defaultChecked={false}
              checked={showOriginalText}
              onChange={(checked) => onShowOriginalTextChange(checked)}
            />
          </div>
        }
      />
      <HotkeyTips />
      {filterTemplates.length > 0 ? (
        <div ref={templateRef}>{templateNodes}</div>
      ) : (
        <div
          style={{
            marginTop: '8px',
            background: '#F0F7FF',
            border: 'solid 1px #336FFF',
          }}
        >
          <TemplateNode
            index={0}
            active
            template={filterSelfTemplate!}
            showLabel={false}
            submitEffect={false}
            doubleMode={showOriginalText}
          />
        </div>
      )}
    </div>
  );
}

// 回复内容：容器组件，从 Context/Formik 取数据并调用 useReplyMsgs 编排展示
export const ReplyMsgs = () => {
  const { values } = useFormikContext<Todo.TodoInstance>();
  const { typeSource } = useContext(TypeSourceContext);
  const formValues: ReplyMsgsFormValues = {
    id: values.id,
    region: values.region,
    type: values.type,
    operateResult: values.operateResult,
  };
  const replyMsgs = useReplyMsgs(typeSource, formValues);
  if (!replyMsgs.shouldRender) {
    return null;
  }
  return <ReplyMsgsView {...replyMsgs} />;
};

type ProcessProps = {
  operationLog: Todo.TodoInstance['operationLog'];
  type: Todo.Type;
  storeId: string;
};
// 处理进度
export function Process(props: ProcessProps) {
  const { operationLog, type, storeId } = props;
  const { t } = useTranslate();
  const [visible, setVisible] = useState(false);
  return (
    <div className="mt-[16px]">
      <ItemHeader
        title={t('processing_history')}
        right={
          <div className="#515359 cursor-pointer" onClick={() => setVisible(true)}>
            {t('todo.明细')}
          </div>
        }
      />
      <div className="mt-[16px]">
        <TodoRecordImplement dataSource={operationLog} simple type={type} storeId={storeId} />
      </div>
      <TodoRecord
        visible={visible}
        onCancel={() => setVisible(false)}
        dataSource={operationLog}
        type={type}
        storeId={storeId}
      />
    </div>
  );
}

// 自定义待办类型
export const CustomType = () => {
  const { t } = useTranslate();
  const { field: typeField } = useSmartField<Todo.CreateTodoParams['type']>('type');
  const { typeSource: _typeSource, taskId, refresh } = useContext(TypeSourceContext);
  const [sortInfo, setSortInfo] = useState<Record<number, number>>(() => {
    return getSortedInfo();
  });
  const { storeId, platform, region } = useContext(ToDoBundleContext);
  const typeSource = useMemo(() => {
    const result = _typeSource.slice().sort((a, b) => {
      const aSort = sortInfo[a.origin.id] || 0;
      const bSort = sortInfo[b.origin.id] || 0;
      return aSort - bSort;
    });
    return getValidTypeSource(result, platform);
  }, [_typeSource, sortInfo, platform]);

  const existNames = _typeSource.map((e) => e.typeLabel);
  const showMore = typeSource.length > 9;
  const [showMoreVisible, setShowMoreVisible] = useState(false);
  const showTypeSource = showMoreVisible ? typeSource : typeSource.slice(0, 9);
  useMount(() => {
    if (typeSource.length > 0) {
      if (!typeField.value) {
        typeField.onChange(typeSource[0].type);
      }
    }
  });
  const getPermission = useGetPermission();
  const permission = useMemo(() => {
    return getPermission('访问待办管理中心', {
      platform,
      region,
      storeId: Number(storeId),
    });
  }, [storeId, platform, region, getPermission]);

  const [instance, open] = useTodoType({
    taskId,
    refresh: () => refresh(),
    existNames,
  });

  const subTypeOptions = useMemo(() => {
    const index = findIndex(typeSource, (o) => o.type === typeField.value);
    return get(typeSource, `${index}.subTypeTrees`) as unknown as any[];
  }, [typeSource, typeField]);
  const { values, setValues } = useFormikContext<Todo.TodoInstance>();

  return (
    <div>
      <ItemHeader
        title={t('choose_todo_type')}
        right={
          permission && (
            <div className="flex justify-end">
              <SortTodoButton
                className="!p-0 !m-0 !h-[24px] !mr-[8px]"
                label={t('review.排序')}
                policies={map(typeSource, 'origin')}
                onSorted={() => {
                  setSortInfo(getSortedInfo());
                }}
              />
              {instance}
              <Button
                onClick={() => {
                  open();
                }}
                type="link"
                className="!p-0 !m-0 !h-[24px]"
              >
                +{t('todo_new_type')}
              </Button>
            </div>
          )
        }
      />
      <FormField name="type" className="!mb-[7px]">
        {({ field }) => {
          return (
            <Radio.Group value={field.value} onChange={field.onChange} className="!mt-[9px]">
              {showTypeSource.map((e) => {
                return (
                  <Radio
                    key={e.type}
                    value={e.type}
                    className="!mb-[10px]"
                    style={{ width: '50%', marginRight: 0, wordBreak: 'break-all' }}
                  >
                    {e.typeLabel}
                  </Radio>
                );
              })}
              <Radio key="__custom__" value={-998}>
                {t('custom')}
              </Radio>
            </Radio.Group>
          );
        }}
      </FormField>

      {showMore && !showMoreVisible && (
        <div
          className="flex items-center justify-center mt-[6px] mb-[16px] cursor-pointer"
          onClick={() => {
            setShowMoreVisible(true);
          }}
        >
          <Icon component={MoreDownSvg} style={{ fontSize: 16, color: '#515359' }} />
          <div className="text-[#515359]">{t('expand_all')}</div>
        </div>
      )}

      {!!subTypeOptions?.length && (
        <FormField name="i-dont-care-fk" label={t('st_3')}>
          {() => {
            const v = [];
            if (values.subType) {
              v.push(values.subType);
              if (values.subType2) {
                v.push(values.subType2);
                if (values.subType3) {
                  v.push(values.subType3);
                }
              }
            }
            return (
              <TodoSubType
                disabled={values.todoStatus === 3}
                dataSource={subTypeOptions}
                value={v}
                onChange={(value) => {
                  const [subType = '', subType2 = '', subType3 = ''] = value;
                  setValues({
                    ...values,
                    subType,
                    subType2,
                    subType3,
                  });
                }}
              />
            );
          }}
        </FormField>
      )}

      {typeField.value === -998 && (
        <FormField
          name="type_name"
          required
          label={t('todo_type_name')}
          validate={(value) => {
            if (!value) return t('todo_type_empty_tip');
            if (existNames.includes(value) || officialTypes.includes(value)) {
              return t('todo_type_repeat_tip');
            }
          }}
        >
          {({ field }) => {
            return (
              <Input
                value={field.value}
                onChange={field.onChange}
                maxLength={100}
                showCount
                placeholder={t('todo_type_empty_tip')}
              />
            );
          }}
        </FormField>
      )}
    </div>
  );
};

// 创建待办Detail
export const NewTodoDetail = () => {
  const { t } = useTranslate();
  const { storeId, platform, assistantId, conversationId } = useContext(ToDoBundleContext);
  const { data: assistants, loading } = useAssistants(storeId);
  const { field: descriptionField } =
    useSmartField<Todo.CreateTodoParams['description']>('description');
  const description = descriptionField.value;
  const { text, images } = useMemo(() => {
    return convertMessageToTextAndImage(description);
  }, [description]);
  const { field: operatorField } = useSmartField<Todo.CreateTodoParams['operator']>('operator');
  useMount(() => {
    if (!operatorField.value) operatorField.onChange(assistantId);
  });

  const customUploadRef = useRef<CustomUploadRef>(null);

  const conversationToggleTextAreaProps = useConversationToggleTextareaProps();

  return (
    <div className="mt-[18px]">
      <ItemHeader title={t('todo_info')} />
      <FormField
        name="operator"
        label={t('todo.受理客服')}
        required
        className="!mt-[8px]"
        validate={(value) => {
          if (!value) {
            return t('todo.请选择x', { x: t('todo.受理客服') });
          }
        }}
      >
        {({ field }) => {
          return (
            <Select
              loading={loading}
              value={field.value}
              onChange={field.onChange}
              options={assistants?.map(({ id, name }: any) => ({ value: id, label: name }))}
            />
          );
        }}
      </FormField>

      <FormField name="description" label={t('ViewBlockList.filter.other.notice')}>
        <TextWithImageInput
          text={text}
          images={images}
          onTextChange={(value) => {
            descriptionField.onChange(convertTextAndImageToMessage(value, images));
          }}
          onImagesChange={(value) => {
            descriptionField.onChange(convertTextAndImageToMessage(text, value));
          }}
          placeholder={t('')}
          textareaProps={conversationToggleTextAreaProps}
          renderImageUpload={({ value, onChange }) => (
            <CustomUpload
              ref={customUploadRef}
              selfHosted
              platform={platform!}
              conversationId={conversationId}
              internalStoreId={storeId}
              onChange={onChange}
              value={value}
              direct={false}
            />
          )}
          onPasteImage={(file) => customUploadRef.current?.handUpload(file)}
        />
        <RelatedDescription isNew />
      </FormField>
    </div>
  );
};

interface TodoSubTypeProps {
  value: string[];
  onChange: (v: string[]) => void;
  dataSource: Todo.SubTypeTreeNode[];
  disabled?: boolean;
  bordered?: boolean;
  className?: string;
}
export const TodoSubType = (props: TodoSubTypeProps) => {
  const { dataSource, value, onChange, disabled, bordered = true, className } = props;
  const { t } = useTranslate();
  const filterPath = (inputValue: string, path: any[]) => {
    return path.some(
      (option) => (option.typeName as string).toLowerCase().indexOf(inputValue.toLowerCase()) > -1,
    );
  };
  const cutedDataSource = useMemo(() => {
    const cut = (each: Todo.SubTypeTreeNode): Todo.SubTypeTreeNode => {
      const { subTypes, ...rest } = each;
      if (subTypes.length === 0) {
        return rest as Todo.SubTypeTreeNode;
      } else {
        return {
          ...rest,
          subTypes: map(subTypes, cut),
        };
      }
    };
    return map(dataSource, cut);
  }, [dataSource]);

  return (
    <Cascader
      className={className}
      bordered={bordered}
      value={value.length === 0 ? undefined : value}
      allowClear
      onChange={(v: unknown) => {
        onChange?.(v as string[]);
      }}
      disabled={disabled}
      showSearch={{ filter: filterPath }}
      options={cutedDataSource}
      fieldNames={{ label: 'typeName', value: 'typeName', children: 'subTypes' }}
      placeholder={t('department.请选择x', { x: t('st_3') })}
    />
  );
};

// 新建和更新都是用同一个组件
interface RelatedDescriptionProps {
  isNew?: boolean; // 是否新建待办
  disabled?: boolean; // 是否禁用
  erpMode?: boolean; // 是否是ERP模式
  erpOrder?: ErpOrderResult; // ERP订单列表
  erpOrderChangeEffect?: () => void; // ERP订单变更时的副作用
  refreshErpOrder?: () => Promise<ErpOrderResult>; // 刷新ERP订单
}
const RelatedDescription = (props: RelatedDescriptionProps) => {
  const { t } = useTranslate();
  const { conversation } = useContext(ToDoBundleContext);
  const {
    isNew = false,
    disabled = false,
    erpMode = false,
    erpOrder,
    erpOrderChangeEffect,
    refreshErpOrder,
  } = props;
  const { values, setValues, handleSubmit } = useFormikContext<Todo.TodoInstance>();
  const valuesRef = useLatest(values);
  const { storeId, externalBuyerId, platform } = useContext(ToDoBundleContext);
  const {
    type,
    relatedTradeId,
    relatedSku,
    relatedReturnId,
    operateResult,
    id,
    region,
    relatedItemId,
  } = values;
  const debouncedHandleSubmit = debounce(handleSubmit, 1500);

  const { data } = useRequest(
    () =>
      getTradeList({
        internal_store_id: storeId,
        external_buyer_id: conv2TradeExternalBuyerId(platform, externalBuyerId),
        page_size: 9999,
        page_no: 1,
      }),
    {
      ready: platform !== 'FACEBOOK',
      refreshDeps: [storeId, externalBuyerId],
    },
  );
  const getTrade = useCallback(
    (tradeId: string) => data?.tradeList?.find((trade) => trade.externalTradeId === tradeId),
    [data],
  );

  const selectedTrade = useMemo(() => {
    return getTrade(relatedTradeId);
  }, [relatedTradeId, getTrade]);
  const getTradeItems = useCallback((selectedTrade?: Conversation.Trade.Vo) => {
    const grouped = groupBy(selectedTrade?.items, (item) => {
      return `${item.externalItemId}___${item.externalSkuId}}`;
    });
    return map(grouped, (items) => {
      const firstItem = items[0];
      return {
        ...firstItem,
        skuId: firstItem.externalSkuId,
        quantity: sumBy(items, 'quantity'),
      };
    });
  }, []);

  const getTradeDefaultItem = useCallback(
    (selectedTrade?: Conversation.Trade.Vo) => {
      if (!selectedTrade) return [] as Todo.RelatedSku[];
      const item = getTradeItems(selectedTrade)?.[0];
      if (!item) return [] as Todo.RelatedSku[];
      return [
        {
          skuName: item.skuName || '',
          sellerSku: item.sellerSku || '',
          quantity: item.quantity,
          itemId: item.externalItemId,
          itemName: item.itemName || '',
          skuId: item.externalSkuId || '',
        } as Todo.RelatedSku,
      ];
    },
    [getTradeItems],
  );
  const tradeItems = useMemo(() => {
    return getTradeItems(selectedTrade);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTrade]);
  useEffect(() => {
    // LEGACY: 迁移老的待办relatedItemId
    if (relatedItemId && (!relatedSku || relatedSku.length === 0)) {
      const item = tradeItems.filter((item) => item.externalItemId === relatedItemId);
      if (item?.length) {
        setValues({
          ...valuesRef.current,
          relatedSku: map(item, (i) => ({
            skuName: i.skuName || '',
            sellerSku: i.sellerSku || '',
            quantity: i.quantity,
            itemId: i.externalItemId,
            itemName: i.itemName || '',
            skuId: i.externalSkuId || '',
          })),
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [relatedSku, relatedItemId, tradeItems, setValues]);

  const returnSnList = useMemo(() => {
    return getReturnSnList(selectedTrade);
  }, [selectedTrade]);

  const refsCurrent = {
    values,
    returnSnList,
    setValues,
  };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;

  useUpdateEffect(() => {
    if (data?.tradeList?.[0]) {
      if (!isNew) return;
      const relatedTradeId = data?.tradeList?.[0].externalTradeId!;
      const trade = getTrade(relatedTradeId);
      const relatedReturnId = getReturnSnList(trade)?.[0];
      const patchValue = {} as Partial<Todo.TodoInstance>;
      if (valuesRef.current.relatedTradeId === undefined) {
        patchValue.relatedTradeId = relatedTradeId;
        if (!valuesRef.current.relatedSku?.length) {
          patchValue.relatedSku = getTradeDefaultItem(trade);
        }
        if (!valuesRef.current.relatedReturnId && relatedReturnId) {
          patchValue.relatedReturnId = relatedReturnId;
        }
      }
      setValues({
        ...valuesRef.current,
        ...patchValue,
      });
    } else {
      if (isNew) {
        setValues({
          ...valuesRef.current,
          relatedTradeId: '',
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isNew]);

  useEffect(() => {
    if (operateResult === 'RETURN_GOODS_REFUND' && !relatedReturnId && isNew === false) {
      const {
        current: { values, returnSnList, setValues },
      } = refs;

      setValues({ ...values, relatedReturnId: returnSnList?.[0] });
    }
  }, [operateResult, relatedReturnId, isNew]);

  // 如果erpOrder 中有商品有补发单，则订单disabled
  const orderDisabledBecauseErpOrder = useMemo(() => {
    if (!erpOrder || !erpOrder.orders) return false;
    return erpOrder.orders?.length > 0;
  }, [erpOrder]);

  const disabledKeys = useMemo(() => {
    if (!erpOrder || !erpOrder.orders) return [];
    return filter(relatedSku, (sku) => {
      if (!sku.skuId) return false;
      return erpOrder.orders.some((order) => {
        return order.skuId === sku.skuId;
      });
    }).map((sku) => `${sku.itemId}___${sku.skuId || ''}`);
  }, [relatedSku, erpOrder]);

  return (
    <div
      className={cx({
        'mt-[24px]': !erpMode,
        'mt-[12px]': erpMode,
      })}
    >
      {(isNew || ![8, 9, 10, 11].includes(type)) && (
        <div className="relative">
          {relatedTradeId && !erpMode && (
            <CopyOutlined
              className="absolute right-0 top-1 cursor-pointer text-[#515359] z-[11]"
              onClick={() => {
                copyText(relatedTradeId).then(() => {
                  message.success(t('todo.已复制'));
                });
              }}
            />
          )}
          <Form.Item
            label={erpMode ? '' : t('todo.关联订单')}
            className="[&:not(:last-child)]:!mb-0"
          >
            <Select
              showSearch
              filterOption={(inputValue, option) => {
                return (option?.value as string)?.includes(inputValue);
              }}
              disabled={disabled || orderDisabledBecauseErpOrder}
              dropdownMatchSelectWidth={false}
              placeholder={t('department.请选择x', {
                x: t('todo.关联订单'),
              })}
              value={relatedTradeId || ''}
              onChange={(relatedTradeId) => {
                const trade = getTrade(relatedTradeId);
                const relatedReturnId = getReturnSnList(trade)?.[0];
                setValues({
                  ...values,
                  relatedTradeId: relatedTradeId,
                  relatedSku: getTradeDefaultItem(trade),
                  relatedReturnId: relatedReturnId || '',
                });
                !isNew && handleSubmit();
                erpOrderChangeEffect?.();
              }}
            >
              <Select.Option value="" key="none">
                {t('无关联订单')}
              </Select.Option>
              {data?.tradeList?.map((item) => {
                return (
                  <Select.Option key={item.externalTradeId} value={item.externalTradeId}>
                    <RelatedTrade>
                      {item.externalTradeId}
                      <TradeStatus trade={item} />
                    </RelatedTrade>
                  </Select.Option>
                );
              })}
            </Select>
          </Form.Item>

          {relatedTradeId && (
            <div className="py-1">
              <TagProduct
                productData={selectedTrade?.items!}
                trade={selectedTrade!}
                conversation={conversation}
                source="TODO"
              />
            </div>
          )}
        </div>
      )}
      {platform !== 'MERCADO' ? (
        <div className="relative">
          {!!relatedSku?.length && !erpMode && (
            <CopyOutlined
              className="absolute right-0 top-1 cursor-pointer text-[#515359] z-[11]"
              onClick={() => {
                copyText(map(relatedSku, 'itemId').join(',')).then(() => {
                  message.success(t('todo.已复制'));
                });
              }}
            />
          )}
          <FormField label={erpMode ? '' : t('todo.关联商品')} name="releateTradeId">
            <RelatedItemSelectStable
              ttsToko={conversation.ttsToko}
              storeId={storeId}
              dataSource={tradeItems}
              disabled={disabled}
              value={relatedSku || undefined}
              searchable={relatedTradeId === ''}
              disabledKeys={disabledKeys}
              erpItemExtraNode={(product) => {
                if (!!returnSnList?.length) {
                  return null;
                }
                if (erpMode) {
                  const targetOrder = find(erpOrder?.orders, (item) => {
                    if (item.source) {
                      const [itemId, skuId] = item.source.split('___');
                      return product.externalItemId === itemId && product.skuId === skuId;
                    }
                    return item.skuId === product.skuId;
                  });
                  return (
                    <ErpOrderWithItem
                      storeId={storeId}
                      itemId={product.externalItemId}
                      renderChildren={(item) => {
                        return (
                          <ErpOrder
                            platform={platform}
                            region={region}
                            storeId={Number(storeId)}
                            erpOrder={targetOrder || erpOrder?.info}
                            sku={product.sku || ''}
                            skuInfo={
                              item
                                ? item.skuInfo
                                : [
                                    {
                                      sku: product.sku || '',
                                      skuId: product.skuId || '',
                                      sellerSku: product.sellerSku || '',
                                    },
                                  ]
                            }
                            todoId={id}
                            tradeId={relatedTradeId}
                            currency={item ? item.currency : selectedTrade?.currency || ''}
                            itemOffsale={!item}
                            item={product as any}
                            onUpdate={refreshErpOrder!}
                            source={`${product.externalItemId}___${product.skuId || ''}`}
                          />
                        );
                      }}
                    />
                  );
                }
                return null;
              }}
              onChange={(value) => {
                setValues({ ...values, relatedSku: value });
                !isNew && debouncedHandleSubmit();
              }}
              trade={selectedTrade}
            />
          </FormField>
        </div>
      ) : null}

      {(isNew || isReturnOrderEnable(operateResult, type)) && (
        <div id="relatedReturnId" className="relative">
          {relatedReturnId && (
            <CopyOutlined
              className="absolute right-0 top-1 cursor-pointer text-[#515359] z-[11]"
              onClick={() => {
                copyText(relatedReturnId).then(() => {
                  message.success(t('todo.已复制'));
                });
              }}
            />
          )}
          <FormField name="relatedReturnId" label={erpMode ? '' : t('related_return')}>
            <Select
              dropdownMatchSelectWidth={false}
              placeholder={t('department.请选择x', {
                x: t('related_return'),
              })}
              disabled={disabled || type === 7}
              value={relatedReturnId || undefined}
              onChange={(value) => {
                setValues({ ...values, relatedReturnId: value });
                !isNew && handleSubmit();
              }}
              options={returnSnList?.map((sn) => ({ value: sn, label: sn }))}
            />
          </FormField>
        </div>
      )}
    </div>
  );
};

RelatedDescription.displayName = 'RelatedDescription';

type ProductLike = Pick<
  Conversation.Trade.Product,
  'externalItemId' | 'sellerSku' | 'itemName' | 'itemPic' | 'quantity' | 'sku'
> & {
  skuId?: string;
};
interface RelatedItemSelectStableProps extends Omit<SelectProps, 'value' | 'onChange'> {
  dataSource?: ProductLike[];
  searchable?: boolean; // 是否可搜索
  value?: Todo.RelatedSku[]; // 关联商品的值
  onChange?: (value: Todo.RelatedSku[]) => void; // 关联商品
  erpMode?: boolean; // 是否是ERP模式
  erpItemExtraNode?: (item: ProductLike) => ReactNode; // 附加的节点
  storeId: string;
  disabledKeys?: string[]; // 禁用的key
  trade?: Conversation.Trade.Vo;
  ttsToko?: boolean;
}
const RelatedItemSelectStable = (props: RelatedItemSelectStableProps) => {
  const { t } = useTranslate();
  const {
    dataSource: _dataSource,
    searchable = false,
    dropdownClassName,
    value,
    onChange,
    disabled,
    erpItemExtraNode,
    storeId,
    disabledKeys,
    trade,
    ttsToko,
    ...restProps
  } = props;
  const valueKeyMap = useMemo(() => {
    return reduce(
      value,
      (acc, item) => {
        const { itemId, skuId } = item;
        acc[`${itemId}___${skuId}`] = true;
        return acc;
      },
      {} as Record<string, boolean>,
    );
  }, [value]);
  const [keywordType, setKeywordType] = useState<KeywordType>(KeywordType.ITEM_NAME);
  const [loadedItems, setLoadedItems] = useState<ProductLike[]>([]);
  const { data: selectedItems } = useQuery({
    queryKey: ['todo', 'selectedItems', valueKeyMap, _dataSource, loadedItems] as const,
    queryFn: async ({ queryKey }) => {
      const valueKeyMap = queryKey[2];
      const _dataSource = queryKey[3] as ProductLike[];
      const loadedItems = queryKey[4] as ProductLike[];
      if (!keys(valueKeyMap).length) return [];
      const found = filter(
        uniqBy([..._dataSource, ...loadedItems], (e) => {
          return `${e.externalItemId}___${e.skuId}`;
        }),
        (item) => {
          return valueKeyMap[`${item.externalItemId}___${item.skuId}`];
        },
      );
      const foundKeyMap = reduce(
        found,
        (acc, item) => {
          acc[`${item.externalItemId}___${item.skuId}`] = true;
          return acc;
        },
        {} as Record<string, boolean>,
      );

      const notFound = filter(keys(valueKeyMap), (key) => {
        return !foundKeyMap[key];
      });

      if (notFound.length) {
        return Promise.all(
          map(notFound, (key) => {
            const [itemId, skuId] = key.split('___');
            return productList({
              internal_store_id: storeId,
              page_no: 1,
              page_size: 1,
              external_item_ids: itemId,
              needStockRule: true,
              warehouseType: 'REISSUE_WAREHOUSE',
            }).then((res) => {
              const item = get(res, 'itemList.0');
              if (!item) return null;
              if (item.skuInfo?.length) {
                const sku = find(item.skuInfo, { skuId });
                return {
                  externalItemId: item.externalItemId,
                  itemName: item.itemName,
                  itemPic: item.imageUrl?.[0],
                  sellerSku: sku?.sellerSku,
                  sku: sku?.skuName,
                  quantity: 1,
                  skuId: sku?.skuId,
                } as ProductLike;
              } else {
                return {
                  externalItemId: item.externalItemId,
                  itemName: item.itemName,
                  itemPic: item.imageUrl?.[0],
                  quantity: 1,
                } as ProductLike;
              }
            });
          }),
        ).then((items) => {
          return [...found, ...filter(items, (e) => e !== null)];
        });
      } else {
        return found;
      }
    },
    enabled: !!value?.length,
  });

  const actualValue = useMemo(() => {
    return map(value, (item) => {
      return `${item.itemId}___${item.skuId || ''}`;
    });
  }, [value]);

  const [searchValue, setSearchValue] = useState('');
  const debouncedSearchValue = useDebounce(searchValue, {
    wait: 800,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['searchProductInTodo', storeId, debouncedSearchValue, keywordType, ttsToko] as const,
    queryFn: ({ queryKey }) => {
      const storeId = queryKey[1];
      const keyword = queryKey[2];
      const keywordType = queryKey[3];
      const ttsToko = queryKey[4];
      const params = {
        internal_store_id: storeId,
        page_no: 1,
        page_size: 50,
        keyword_type: keywordType,
        keyword,
        needStockRule: true,
        order_by: 'SALE' as const,
        ttsToko,
      };

      return productList(params).then((res) => {
        const items = get(res, 'itemList', []) as Conversation.Product[];
        const allItemSkus = reduce(
          items,
          (acc, cur) => {
            if (cur.skuInfo?.length) {
              cur.skuInfo.forEach((sku) => {
                acc.push({
                  externalItemId: cur.externalItemId,
                  itemName: cur.itemName,
                  itemPic: cur.imageUrl?.[0],
                  sellerSku: sku.sellerSku,
                  sku: sku.skuName,
                  quantity: 1,
                  skuId: sku.skuId,
                } as ProductLike);
              });
            } else {
              acc.push({
                externalItemId: cur.externalItemId,
                itemName: cur.itemName,
                itemPic: cur.imageUrl?.[0],
                quantity: 1,
              } as ProductLike);
            }
            return acc;
          },
          [] as ProductLike[],
        );
        if (keywordType === KeywordType.SKU) {
          return filter(allItemSkus, (item) => {
            return !!item.sku?.toLowerCase().includes(keyword.toLowerCase());
          });
        } else if (keywordType === KeywordType.ITEM_NO) {
          return filter(allItemSkus, (item) => {
            return !!item.sellerSku?.toLowerCase().includes(keyword.toLowerCase());
          });
        } else {
          return allItemSkus;
        }
      });
    },
    enabled: searchable,
  });

  useEffect(() => {
    if (data && data.length) {
      setLoadedItems(
        produce((draft) => {
          return uniqBy([...draft, ...data], (item) => {
            return `${item.externalItemId}___${item.skuId}`;
          });
        }),
      );
    }
  }, [data]);

  const dataSource = useMemo(() => {
    return searchable
      ? uniqBy([...(selectedItems || []), ...(data || [])], (e) => {
          return `${e.externalItemId}___${e.skuId}`;
        })
      : _dataSource || [];
  }, [searchable, selectedItems, data, _dataSource]);

  return (
    <div>
      <Input.Group compact className="!flex">
        {searchable && (
          <Select
            className="max-w-[140px]"
            dropdownMatchSelectWidth={false}
            value={keywordType}
            onChange={(val) => {
              setKeywordType(val);
            }}
            optionLabelProp="simple"
            options={[
              {
                value: KeywordType.ITEM_NAME,
                label: t('sider.商品名称'),
                simple: t('sider.商品名称'),
              },
              { value: KeywordType.ITEM_ID, label: t('sider.商品ID'), simple: t('sider.商品ID') },
              {
                value: KeywordType.SKU,
                label: 'SKU',
                simple: 'SKU',
              },
              {
                value: KeywordType.ITEM_NO,
                label: `${t('sider.货号')}`,
                simple: t('sider.货号'),
              },
            ]}
          ></Select>
        )}
        <Select<string[]>
          className="!flex-1"
          dropdownMatchSelectWidth={false}
          optionLabelProp="label"
          placeholder={t('department.请选择x', {
            x: t('todo.关联商品'),
          })}
          filterOption={false}
          dropdownClassName={cx(
            dropdownClassName,
            css`
              .ant-select-item-option-selected:not(.ant-select-item-option-disabled) {
                background: #fff;
              }
              .ant-select-item-option-state {
                display: none;
              }
              .e28-checked {
                display: none;
              }
              .ant-select-item-option-selected {
                .e28-checked {
                  display: block;
                }
              }
              .ant-select-item {
                padding-top: 0 !important;
                padding-bottom: 0 !important;
                .ant-select-item-option-content {
                  border-bottom: 1px solid #f0f2f5;
                }
                &:last-child {
                  .ant-select-item-option-content {
                    border-bottom: none;
                  }
                }
              }
            `,
          )}
          mode="multiple"
          maxTagCount="responsive"
          value={actualValue}
          onChange={(value) => {
            const selected = map(
              filter(dataSource, (item) => {
                return value.includes(`${item.externalItemId}___${item.skuId || ''}`);
              }),
              (each) => {
                return {
                  itemId: each.externalItemId,
                  sellerSku: each.sellerSku || '',
                  skuName: each.sku || '',
                  quantity: each.quantity,
                  itemName: each.itemName || '',
                  skuId: each.skuId || '',
                } as Todo.RelatedSku;
              },
            );
            onChange?.(selected);
          }}
          loading={searchable && isLoading}
          showSearch={searchable}
          disabled={disabled}
          onSearch={(value) => {
            setSearchValue(value);
          }}
          {...restProps}
        >
          {dataSource?.map((item: any) => {
            const key = `${item.externalItemId}___${item.skuId || ''}`;
            return (
              <Select.Option
                key={key}
                value={key}
                label={item.itemName}
                disabled={disabledKeys?.includes(key)}
              >
                <div className="flex items-center py-[12px] justify-between">
                  <div className="flex">
                    <SafeImage
                      hasSmallPic="both"
                      src={item.itemPic}
                      className="!w-[48px] !h-[48px]"
                    />
                    <div className="max-w-[272px] ml-[8px] !font-normal !text-[12px]">
                      <div
                        className="overflow-hidden truncate leading-[18px] mb-[3px]"
                        title={item.itemName}
                      >
                        {item.itemName}
                      </div>
                      {item.skuId && (
                        <div className="flex gap-1 text-[12px] text-[#84868C] mt-[3px]">
                          <span className="truncate" title={item.sku}>
                            [{item.sku}]
                          </span>
                          <span className="truncate" title={item.sellerSku}>
                            [{item.sellerSku}]
                          </span>
                          {item.quantify && (
                            <span className="flex-shrink-0">[{`x${item.quantity}`}]</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <CheckOutlined className="flex-shrink-0 ml-[8px] !text-[#3370FF] e28-checked" />
                </div>
              </Select.Option>
            );
          })}
        </Select>
      </Input.Group>

      {!!selectedItems?.length && (
        <div className="mt-2">
          {selectedItems?.map((each, index) => {
            return (
              <div className="flex gap-2 bg-[#f7f9fa] border-0 border-b border-solid border-[#d3d7e0] last:border-b-0 p-3">
                <div className="w-[48px] h-[48px]">
                  <SafeImage hasSmallPic="both" src={each.itemPic} width={48} height={48} />
                </div>
                <div className="overflow-hidden flex-1">
                  <div className="truncate" title={each.itemName}>
                    {each.itemName}
                  </div>
                  {each.skuId && (
                    <div className="flex gap-1 text-[12px] text-[#84868C] mt-[3px]">
                      <span className="truncate" title={each.sku}>
                        [{each.sku}]
                      </span>
                      <span className="truncate" title={each.sellerSku}>
                        [{each.sellerSku}]
                      </span>
                      {each.quantity && (
                        <span className="flex-shrink-0">[{`x${each.quantity}`}]</span>
                      )}
                    </div>
                  )}
                  <div className="flex items-center mt-[8px] gap-1">
                    <span className="text-[12px]">{t('trade.数量')}</span>
                    <SimpleInputNumber
                      disabled={disabled}
                      min={1}
                      max={999}
                      value={get(value, `${index}.quantity`, 0)}
                      onChange={(val) => {
                        const newValue = [...(value || [])];
                        newValue[index] = {
                          ...newValue[index],
                          quantity: val,
                        };
                        onChange?.(newValue);
                      }}
                    />
                  </div>
                  <div className="[&:not(:empty)]:mt-2">{erpItemExtraNode?.(each)}</div>
                  {trade && (
                    <div className="[&:not(:empty)]:mt-2 [&_div]:!bg-[#fff]">
                      <ProductTagList trade={trade} productData={each as any} source="TODO" />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

RelatedItemSelectStable.displayName = 'RelatedItemSelectStable';

interface SimpleInputNumberProps {
  min?: number;
  max?: number;
  value?: number;
  onChange?: (value: number) => void;
  disabled?: boolean;
}
export const SimpleInputNumber = (props: SimpleInputNumberProps) => {
  const { min, max, value = 0, onChange, disabled = false } = props;
  const { t } = useTranslate();
  return (
    <div
      className={cx('rounded-[4px] bg-[#fff] flex items-center', {
        'cursor-not-allowed': disabled,
        'text-[#F7F9FA]': disabled,
      })}
    >
      <div
        className={cx('w-[28px] h-[28px] flex items-center justify-center cursor-pointer', {
          '!cursor-not-allowed text-[#cccdcf]': disabled || (min !== undefined && value <= min),
        })}
        onClick={() => {
          if (disabled) return;
          if (min !== undefined && value <= min) return message.warn(t('最少x件', { x: min }));
          onChange?.(value - 1);
        }}
      >
        <MinusOutlined />
      </div>
      <div className="px-2 w-fit flex items-center justify-center text-[#232426]">{value}</div>
      <div
        className={cx('w-[28px] h-[28px] flex items-center justify-center cursor-pointer', {
          '!cursor-not-allowed text-[#cccdcf]': disabled || (max !== undefined && value >= max),
        })}
        onClick={() => {
          if (disabled) return;
          if (max !== undefined && value >= max) return message.warn(t('最对x件', { x: max }));
          onChange?.(value + 1);
        }}
      >
        <PlusOutlined />
      </div>
    </div>
  );
};

SimpleInputNumber.displayName = 'SimpleInputNumber';

interface ErpOrderWithItemProps {
  storeId: string;
  itemId: string;
  renderChildren: (item?: Conversation.Product) => JSX.Element;
}
const ErpOrderWithItem = (props: ErpOrderWithItemProps) => {
  const { renderChildren, storeId, itemId } = props;
  const { data: item = undefined, isLoading } = useQuery({
    queryKey: ['todo', 'erpOrderWithItem', storeId, itemId] as const,
    queryFn: async ({ queryKey }) => {
      const storeId = queryKey[2];
      const itemId = queryKey[3];
      return productList({
        internal_store_id: storeId,
        page_no: 1,
        page_size: 1,
        keyword_type: 1,
        keyword: itemId,
        needStockRule: true,
        warehouseType: 'REISSUE_WAREHOUSE',
      })
        .then(({ itemList }) => {
          if (itemList?.length) {
            return itemList[0];
          }
        })
        .catch(() => {});
    },
    enabled: !!storeId && !!itemId,
  });
  if (!isLoading) {
    return renderChildren(item);
  } else {
    return null;
  }
};
ErpOrderWithItem.displayName = 'ErpOrderWithItem';
